import { Suspense, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react'
import { abortOnNavigation, DirectoryRefreshError } from '../router/flashcards-rpc-options'
import { useMutation, useSuspenseQuery } from '@w/react-query'
import { queryOptions } from '@w/react-query/db'
import { s } from '@w/schema'
import { useNavigate, useParams, useRouterContext } from '../router/flashcards-fe.router-bindings'
import type { SpaceResource } from '../router/spaces-resources'
import { useUserSpaces } from '../router/authenticated-layout'
import { DatabaseGrid } from './database-grid'
import { WorkspaceSyncAlerts } from '../cards/workspace-sync-status'
import { databaseSpaceRootOptions, databasesOptions } from './database-space'
import { AppLayout, AppNavigation, PageExpansionProvider, buildPageTree } from '../pages/page'
import { contentResources, SpaceQueries, spaceTreeOptions, useSpacesView } from '../db/spaces-queries'
import type { PageMeta } from '../db/page-read'
import { dueFlashcardCountOptions, workspaceTeamspacesOptions } from '../router/page-view'
import { createPageCommand } from '../router/page-commands'
import { pageRouteParams } from '../router/page-route-params'
import { useSignOut } from '../shell/app'
import { ulid, unknownErrorToError } from '@w/utils'
import { blockId } from '@w/content-blocks/src/block'
import { ReplicaTransportError } from '@w/db-turso-browser-adapter/worker'
import { Sidebar, SidebarInset, SidebarRail } from '@w/ui/components/sidebar'

// Module scope (Zod: define schemas once): inline schemas paid construction on every call.
const placementSchema = s.object({ pageId: s.string(), parentPageId: s.string() })

export function DatabasePage(): ReactElement {
  const ctx = useRouterContext()
  const params = useParams({ from: '/$workspaceId/spaces/$spaceId/databases/$table' })
  const spaces = useUserSpaces()
  const { data: view, error } = useSpacesView(spaces, params.workspaceId, ctx.queryClient)
  if (error) throw error
  const resources = view ? contentResources(view) : []
  const resource = resources.find(value => value.space.id === params.spaceId)
  return <AppLayout>{resource ? <SpaceQueries resources={resources} options={spaceTreeOptions}>
    {trees => {
      const failed = trees.find(tree => tree.error)
      if (failed?.error) throw failed.error
      return <PageExpansionProvider key={resource.connectionId}><DatabaseContent resource={resource} resources={resources} pages={trees.flatMap(tree => tree.data ?? [])} /></PageExpansionProvider>
    }}
  </SpaceQueries> : view ? <main className="p-6">Database unavailable</main> : null}</AppLayout>
}

function DatabaseContent({ resource, resources, pages }: { resource: SpaceResource; resources: SpaceResource[]; pages: PageMeta[] }) {
  const ctx = useRouterContext()
  const params = useParams({ from: '/$workspaceId/spaces/$spaceId/databases/$table' })
  const navigate = useNavigate()
  const signOut = useSignOut()
  const [signOutError, setSignOutError] = useState<Error | null>(null)
  const [workspaceError, setWorkspaceError] = useState<unknown>(null)
  const selectionRef = useRef<AbortController | null>(null)
  const { data: directory } = useSuspenseQuery(ctx.rpc.workspace.list.queryOptions(), ctx.queryClient)
  const { data: teamspaces } = useSuspenseQuery(workspaceTeamspacesOptions(resource), resource.queryClient)
  const { data: dueCounts } = useSuspenseQuery(dueFlashcardCountOptions(ctx, params), resource.queryClient)
  const { data: placements } = useSuspenseQuery(queryOptions(
    resource.db.query`SELECT ${resource.db.pageRelations.toPageId} AS pageId, ${resource.db.pageRelations.fromPageId} AS parentPageId FROM ${resource.db.pageRelations} WHERE ${resource.db.pageRelations.type.eq('child')}`,
    placementSchema,
  ), resource.queryClient)
  const { data: root } = useSuspenseQuery(databaseSpaceRootOptions(resource), resource.queryClient)
  const { data: access } = useSuspenseQuery(ctx.rpc.page.levels.queryOptions({ workspaceId: params.workspaceId }), ctx.queryClient)
  const level = access.levels[root] ?? 0
  // A rename this device made can be undone by a server refusal (rollback) after the route moved to the new name, or
  // redone by Re-apply (dialog or failure panel; the store outlives this page, so a Re-apply after leaving or reloading
  // the database is followed too, databases/database-renames.ts). When the routed table is gone and the other name of
  // that rename exists, the open database follows it instead of rendering a table that no longer exists (a refusal that
  // lands before the rename settles is handled in DatabaseRename). Key the grid by the resolved table: following
  // a rename and then updating the route is one mount, with no discarded cold reads. The rename expires once
  // the route leaves both names, so a later table that reuses a name is never followed.
  // Known gap: only the latest rename is remembered, so two refused renames in a row (A->B->C) are not followed back.
  const { data: { followed } } = useSuspenseQuery(resource.databaseRenames.options(), resource.queryClient)
  const rename = followed && (params.table === followed.from || params.table === followed.to) ? followed : null
  useEffect(() => {
    if (followed && !rename) resource.databaseRenames.expire(followed)
  }, [followed, rename, resource.databaseRenames])
  // The list can be a cached copy that is still refetching (stale-while-revalidate after its subscription lapsed, e.g.
  // while Settings was open), so opening the old name of a remembered rename (Re-apply from Settings → Sync problems)
  // decides only once the refetch settles; meanwhile the grid of a table that may no longer exist is not rendered. The
  // grid's own Suspense keeps its cold reads (after that unmount or any table change) from hiding the navigation and the
  // failure notice behind the app's root boundary.
  const { data: tables, isFetching: tablesRefetching } = useSuspenseQuery(databasesOptions(resource), resource.queryClient)
  // Only the first refetch this page sees for a rename can carry a stale cached list; once one settled, later refetches
  // (any schema change) keep the grid and its state (render-time state adjustment, react.dev "storing information from
  // previous renders").
  const [listed, setListed] = useState({ rename, fetched: tablesRefetching, settled: false })
  if (listed.rename !== rename) setListed({ rename, fetched: tablesRefetching, settled: false })
  else if (tablesRefetching && !listed.fetched) setListed({ ...listed, fetched: true })
  else if (!tablesRefetching && listed.fetched && !listed.settled) setListed({ ...listed, settled: true })
  const deciding = rename !== null && params.table === rename.from && tablesRefetching && !(listed.rename === rename && listed.settled)
  const table = deciding ? params.table : followRename(params.table, rename, tables.map(item => item.name))
  useEffect(() => {
    if (table !== params.table) navigate({ to: '/$workspaceId/spaces/$spaceId/databases/$table', params: { ...params, table }, replace: true })
  }, [navigate, params, table])
  const createPage = useMutation({ mutationFn: async (input: { workspaceId: string; parentId: string | null; teamspaceId?: string; title: string; signal?: AbortSignal }) => {
    await ctx.writes.flush()
    const pageId = ulid()
    await createPageCommand(ctx, { workspaceId: input.workspaceId, pageId, parentPageId: input.parentId ?? undefined,
      teamspaceId: input.teamspaceId, title: input.title, blocks: [{ id: blockId(), type: 'p', children: [] }],
    })
    await ctx.testSupport?.beforeCreatePage?.(pageId)
    if (!input.signal?.aborted) navigate({ to: '/$workspaceId/spaces/$spaceId/pages/$pageId', params: pageRouteParams(ctx, input.workspaceId, pageId), search: { focusTitle: pageId } })
  } })
  async function openPage(pageId: string, spaceId: string) {
    await ctx.writes.flush()
    navigate({ to: '/$workspaceId/spaces/$spaceId/pages/$pageId', params: pageRouteParams(ctx, spaceId, pageId) })
  }
  function beginSelection() {
    selectionRef.current?.abort()
    const selection = new AbortController()
    selectionRef.current = selection
    return selection
  }
  /** Same token rule as pages/page.tsx handleCreateWorkspace: a later choice or sign-out wins over a late create. */
  async function createWorkspace(name: string) {
    const selection = beginSelection()
    // A navigation until the whole create and open settles wins; the committed workspace only joins the directory.
    const navigated = abortOnNavigation(selection)
    try {
      let workspaceId: string
      try {
        ({ workspaceId } = await ctx.rpcClient.workspace.create.mutate({ name }))
      } catch (error) {
        if (!(error instanceof DirectoryRefreshError)) throw error
        if (!selection.signal.aborted) setWorkspaceError(error.cause)
        return
      }
      if (!selection.signal.aborted) await changeWorkspace(workspaceId, selection)
    } finally { navigated.stop() }
  }
  async function changeWorkspace(workspaceId: string, selection = beginSelection()) {
    setWorkspaceError(null)
    try {
      await ctx.writes.flush()
      if (selection.signal.aborted) return
      await ctx.openWorkspace?.(workspaceId)
      if (selection.signal.aborted) return
      await ctx.spaces.trigger(workspaceId, true, ctx.tel).catch(() => {})
      if (selection.signal.aborted) return
      const status = await ctx.spaces.syncStatusOptions(workspaceId).queryFn()
      if (status.error instanceof ReplicaTransportError && status.error.status === 403) throw status.error
      const nextResources = contentResources(ctx.spaces.snapshot(workspaceId))
      const rows = await Promise.all(nextResources.map(value => value.queryClient.fetchQuery(spaceTreeOptions(value))))
      if (selection.signal.aborted) return
      const next = rows.flat().find(page => !teamspaces.some(team => team.id === page.id))
      if (next) { await openPage(next.id, next.spaceId); return }
      const own = nextResources.find(value => value.space.kind === 'private' && value.space.ownerUserId === ctx.getUser().id)
      if (!own) throw new Error('Private space unavailable')
      await createPage.mutateAsync({ workspaceId: own.space.id, parentId: null, title: 'Untitled page', signal: selection.signal })
    } catch (error) { if (!selection.signal.aborted) setWorkspaceError(error) }
  }
  async function handleSignOut() {
    selectionRef.current?.abort()
    try { setSignOutError(null); await signOut(); navigate({ to: '/login' }) }
    catch (error) { setSignOutError(unknownErrorToError(error)) }
  }
  async function openTraining() {
    await ctx.writes.flush()
    navigate({ to: '/$workspaceId/training', params: { workspaceId: params.workspaceId }, search: { total: String(dueCounts[0]?.count ?? 0) } })
  }
  return <DatabaseLayout navigation={
      <AppNavigation resource={resource} resources={resources} workspaces={directory.workspaces}
        tree={buildPageTree(pages.filter(page => page.kind !== 'databaseRow'), placements)} teamspaces={teamspaces}
        dueCount={dueCounts[0]?.count ?? 0} invitationCount={directory.invites.length}
        pending={createPage.isPending} errors={{ signOut: signOutError, workspace: workspaceError, save: createPage.error ? unknownErrorToError(createPage.error) : null }}
        onWorkspaceChange={changeWorkspace} onCreateWorkspace={createWorkspace} onSignOut={handleSignOut} onOpenTraining={openTraining}
        onCreatePage={input => createPage.mutateAsync(input)} onOpenPage={openPage} beforeCreate={() => ctx.writes.flush()} />}>
      <WorkspaceSyncAlerts workspaceId={params.workspaceId} className="mb-4" />
      {level < 1 ? 'Database unavailable' : deciding ? null : <Suspense fallback={null}><DatabaseGrid key={table} resource={resource} table={table} canEdit={level >= 2} onOpenRow={pageId => { navigate({ to: '/$workspaceId/spaces/$spaceId/pages/$pageId', params: { workspaceId: params.workspaceId, spaceId: params.spaceId, pageId } }) }} onRenamed={next => { resource.databaseRenames.follow({ from: table, to: next }); navigate({ to: '/$workspaceId/spaces/$spaceId/databases/$table', params: { ...params, table: next } }) }} onDeleted={() => { navigate({ to: '/$workspaceId/spaces/$spaceId/pages', params }) }} /></Suspense>}
  </DatabaseLayout>
}

function followRename(table: string, rename: { from: string; to: string } | null, tables: readonly string[]): string {
  if (!rename || tables.includes(table)) return table
  const other = table === rename.to ? rename.from : table === rename.from ? rename.to : table
  return tables.includes(other) ? other : table
}

/**
 * The database route's shell inside `AppLayout`, whose column wrapper would stack the
 * sidebar's in-flow gap above the inset and leave the fixed sidebar over the grid.
 * The row wrapper is the one `Page` uses for the same pair.
 */
export function DatabaseLayout({ navigation, children }: { navigation: ReactNode; children: ReactNode }): ReactElement {
  return <div className="flex min-h-0 min-w-0 flex-1">
    <Sidebar collapsible="offcanvas">
      {navigation}
      <SidebarRail />
    </Sidebar>
    <SidebarInset className="bg-surface-1 min-w-0">
      <main className="w-full min-w-0 flex-1 px-6 pb-12 pt-16 md:px-12 md:pt-32 xl:pl-64 2xl:pl-72">{children}</main>
    </SidebarInset>
  </div>
}
